<template>
  <div class="pycad-settings">
    <div class="pycad-settings__header">
      <div class="pycad-settings__icon">
        <i class="mdi mdi-cog-outline"></i>
      </div>
      <div>
        <div class="pycad-settings__title">Settings</div>
        <div class="pycad-settings__subtitle">
          Viewer preferences and connections
        </div>
      </div>
    </div>

    <!-- General -->
    <section class="pycad-settings__section">
      <div class="pycad-settings__section-title">General</div>

      <button
        type="button"
        class="pycad-settings__row pycad-settings__row--button"
        @click="openKeyboardShortcuts"
      >
        <i class="mdi mdi-keyboard-outline pycad-settings__row-icon"></i>
        <div class="pycad-settings__row-text">
          <span class="pycad-settings__row-label">
            Keyboard Shortcuts & View Controls
          </span>
          <span class="pycad-settings__row-hint">
            See all available hotkeys and mouse controls
          </span>
        </div>
        <i class="mdi mdi-chevron-right pycad-settings__row-chevron"></i>
      </button>

      <div class="pycad-settings__row">
        <i class="mdi mdi-camera-retake-outline pycad-settings__row-icon"></i>
        <div class="pycad-settings__row-text">
          <span class="pycad-settings__row-label">Camera Auto Reset</span>
          <span class="pycad-settings__row-hint">
            Keep the camera as-is when switching datasets
          </span>
        </div>
        <button
          type="button"
          class="pycad-toggle"
          :class="{ 'pycad-toggle--on': disableCameraAutoReset }"
          role="switch"
          :aria-checked="disableCameraAutoReset"
          @click="disableCameraAutoReset = !disableCameraAutoReset"
        >
          <span class="pycad-toggle__thumb"></span>
        </button>
      </div>

      <div v-if="errorReportingConfigured" class="pycad-settings__row">
        <i class="mdi mdi-bug-outline pycad-settings__row-icon"></i>
        <div class="pycad-settings__row-text">
          <span class="pycad-settings__row-label">Error Reporting</span>
          <span class="pycad-settings__row-hint">
            Send anonymous crash reports to help improve stability
          </span>
        </div>
        <button
          type="button"
          class="pycad-toggle"
          :class="{ 'pycad-toggle--on': reportingEnabled }"
          role="switch"
          :aria-checked="reportingEnabled"
          @click="reportingEnabled = !reportingEnabled"
        >
          <span class="pycad-toggle__thumb"></span>
        </button>
      </div>
    </section>

    <!-- DICOMWeb -->
    <section class="pycad-settings__section">
      <div class="pycad-settings__section-title">DICOMWeb</div>
      <div class="pycad-settings__section-hint">
        Connect to a DICOMWeb server to browse and load studies remotely.
      </div>

      <label class="pycad-settings__field-label" for="dicomweb-name">
        Host Display Name
      </label>
      <div class="pycad-settings__field">
        <i class="mdi mdi-tag-outline"></i>
        <input
          id="dicomweb-name"
          v-model="dicomWeb.hostName"
          type="text"
          placeholder="My PACS"
          spellcheck="false"
          autocomplete="off"
        />
      </div>

      <label class="pycad-settings__field-label" for="dicomweb-host">
        Host Address
      </label>
      <div class="pycad-settings__field">
        <i class="mdi mdi-server-network-outline"></i>
        <input
          id="dicomweb-host"
          v-model="dicomWeb.savedHost"
          type="text"
          placeholder="https://example.com/dicom-web"
          spellcheck="false"
          autocomplete="off"
        />
      </div>
    </section>

    <!-- Remote server (compiled in only for remote-enabled builds) -->
    <section v-if="hasRemoteServer" class="pycad-settings__section">
      <div class="pycad-settings__section-title">
        Remote Server
        <a
          class="pycad-settings__help"
          href="https://kitware.github.io/VolView/doc/server.html"
          target="_blank"
          title="Server documentation"
        >
          <i class="mdi mdi-help-circle-outline"></i>
        </a>
      </div>
      <div class="pycad-settings__section-hint">
        Offload processing to a remote compute server. Make sure you trust it!
      </div>

      <label class="pycad-settings__field-label" for="remote-url">
        Server URL
      </label>
      <div class="pycad-settings__field">
        <i class="mdi mdi-link-variant"></i>
        <input
          id="remote-url"
          v-model="serverUrl"
          type="text"
          placeholder="wss://example.com"
          spellcheck="false"
          autocomplete="off"
        />
      </div>

      <div class="pycad-settings__connect-row">
        <button
          type="button"
          class="pycad-settings__connect-btn"
          :disabled="connState === ConnectionState.Pending"
          @click="toggleConnection"
        >
          <i
            v-if="connState === ConnectionState.Pending"
            class="mdi mdi-loading pycad-settings__spin"
          ></i>
          <span>{{ connectBtnText }}</span>
        </button>
        <span
          class="pycad-settings__conn-status"
          :class="`pycad-settings__conn-status--${connStateClass}`"
        >
          <span class="pycad-settings__conn-dot"></span>
          {{ connStatus }}
        </span>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';

import { useKeyboardShortcutsStore } from '@/src/store/keyboard-shortcuts';
import { useViewCameraStore } from '@/src/store/view-configs/camera';
import { useDicomWebStore } from '@/src/store/dicom-web/dicom-web-store';
import { ConnectionState, useServerStore } from '@/src/store/server';
import { isFeatureCompiled } from '@/src/features/featureFlags';
import {
  useErrorReporting,
  errorReportingConfigured,
} from '../utils/errorReporting';

export default defineComponent({
  setup() {
    const errorReportingStore = useErrorReporting();
    const reportingEnabled = ref(!errorReportingStore.disableReporting);
    watch(reportingEnabled, (enabled) => {
      errorReportingStore.disableReporting = !enabled;
    });

    const { disableCameraAutoReset } = storeToRefs(useViewCameraStore());

    const keyboardStore = useKeyboardShortcutsStore();
    const openKeyboardShortcuts = () => {
      keyboardStore.settingsOpen = true;
    };

    // --- DICOMWeb --- //
    const dicomWeb = useDicomWebStore();

    // --- remote server --- //
    const hasRemoteServer = isFeatureCompiled('remoteServer');
    const serverStore = useServerStore();
    const { connState } = storeToRefs(serverStore);

    const serverUrl = computed({
      get: () => serverStore.url,
      set: (url: string) => serverStore.setUrl(url),
    });

    const StatusToText: Record<ConnectionState, string> = {
      [ConnectionState.Connected]: 'Connected',
      [ConnectionState.Disconnected]: 'Disconnected',
      [ConnectionState.Pending]: 'Connecting...',
    };
    const connStatus = computed(() => StatusToText[connState.value]);

    const StatusToClass: Record<ConnectionState, string> = {
      [ConnectionState.Connected]: 'connected',
      [ConnectionState.Disconnected]: 'disconnected',
      [ConnectionState.Pending]: 'pending',
    };
    const connStateClass = computed(() => StatusToClass[connState.value]);

    function toggleConnection() {
      if (connState.value === ConnectionState.Connected) {
        serverStore.disconnect();
      } else if (connState.value === ConnectionState.Disconnected) {
        serverStore.connect();
      }
    }

    const connectBtnText = computed(() => {
      return connState.value === ConnectionState.Connected
        ? 'Disconnect'
        : 'Connect';
    });

    return {
      reportingEnabled,
      errorReportingConfigured,
      openKeyboardShortcuts,
      disableCameraAutoReset,
      dicomWeb,
      hasRemoteServer,
      serverUrl,
      connState,
      ConnectionState,
      connStatus,
      connStateClass,
      connectBtnText,
      toggleConnection,
    };
  },
});
</script>

<style scoped>
.pycad-settings {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px;
  background: var(--pycad-elevated, #161616);
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius, 12px);
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--pycad-text-primary, #fff);
  max-height: 80vh;
  overflow-y: auto;
}

.pycad-settings__header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-right: 40px; /* keep clear of the dialog's floating close button */
}

.pycad-settings__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: var(--pycad-radius-sm, 8px);
  background: var(--pycad-primary-dim, rgba(255, 153, 0, 0.12));
  border: 1px solid var(--pycad-primary-border, rgba(255, 153, 0, 0.3));
  color: var(--pycad-primary, #ff9900);
  font-size: 20px;
}

.pycad-settings__title {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.pycad-settings__subtitle {
  margin-top: 2px;
  font-size: 12px;
  color: var(--pycad-text-secondary, #a0a0a0);
}

/* --- sections --- */

.pycad-settings__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  background: var(--pycad-surface, #0d0d0d);
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius, 10px);
}

.pycad-settings__section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--pycad-primary, #ff9900);
}

.pycad-settings__section-hint {
  font-size: 11.5px;
  color: var(--pycad-text-muted, #777);
  margin-bottom: 4px;
}

.pycad-settings__help {
  display: inline-flex;
  color: var(--pycad-text-muted, #777);
  text-decoration: none;
  font-size: 13px;
  transition: color 0.12s ease;
}

.pycad-settings__help:hover {
  color: var(--pycad-text-primary, #fff);
}

/* --- rows (toggles / buttons) --- */

.pycad-settings__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: var(--pycad-elevated, #161616);
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius-sm, 8px);
}

.pycad-settings__row--button {
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  width: 100%;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
}

.pycad-settings__row--button:hover {
  border-color: var(--pycad-primary-border, rgba(255, 153, 0, 0.45));
}

.pycad-settings__row-icon {
  flex-shrink: 0;
  font-size: 17px;
  color: var(--pycad-text-secondary, #a0a0a0);
}

.pycad-settings__row-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.pycad-settings__row-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--pycad-text-primary, #fff);
}

.pycad-settings__row-hint {
  font-size: 11px;
  color: var(--pycad-text-muted, #777);
}

.pycad-settings__row-chevron {
  color: var(--pycad-text-muted, #777);
  font-size: 16px;
}

/* --- custom toggle --- */

.pycad-toggle {
  position: relative;
  flex-shrink: 0;
  width: 38px;
  height: 21px;
  border-radius: 999px;
  background: var(--pycad-border, #333);
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.18s ease;
  padding: 0;
}

.pycad-toggle__thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #999;
  transition:
    transform 0.18s ease,
    background 0.18s ease;
}

.pycad-toggle--on {
  background: var(--pycad-primary, #ff9900);
}

.pycad-toggle--on .pycad-toggle__thumb {
  transform: translateX(17px);
  background: #111;
}

/* --- fields --- */

.pycad-settings__field-label {
  margin-top: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--pycad-text-muted, #777);
}

.pycad-settings__field {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  background: var(--pycad-elevated, #161616);
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius-sm, 8px);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.pycad-settings__field:focus-within {
  border-color: var(--pycad-primary-border, rgba(255, 153, 0, 0.45));
  box-shadow: 0 0 0 3px rgba(255, 153, 0, 0.08);
}

.pycad-settings__field i {
  flex-shrink: 0;
  font-size: 14px;
  color: var(--pycad-text-muted, #777);
}

.pycad-settings__field input {
  flex: 1;
  min-width: 0;
  padding: 10px 0;
  font-size: 13px;
  font-family: inherit;
  color: var(--pycad-text-primary, #fff);
  background: transparent;
  border: none;
  outline: none;
}

.pycad-settings__field input::placeholder {
  color: var(--pycad-text-muted, #555);
}

/* --- connect row --- */

.pycad-settings__connect-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 4px;
}

.pycad-settings__connect-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 18px;
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  letter-spacing: 0.02em;
  color: #111;
  background: var(--pycad-primary, #ff9900);
  border: none;
  border-radius: var(--pycad-radius-sm, 8px);
  cursor: pointer;
  transition: filter 0.15s ease;
}

.pycad-settings__connect-btn:hover:not(:disabled) {
  filter: brightness(1.08);
}

.pycad-settings__connect-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.pycad-settings__spin {
  animation: pycad-settings-spin 0.9s linear infinite;
}

@keyframes pycad-settings-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.pycad-settings__conn-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--pycad-text-secondary, #a0a0a0);
}

.pycad-settings__conn-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--pycad-text-muted, #666);
}

.pycad-settings__conn-status--connected .pycad-settings__conn-dot {
  background: #4ade80;
}

.pycad-settings__conn-status--pending .pycad-settings__conn-dot {
  background: var(--pycad-primary, #ff9900);
}
</style>
